/* ── Who is typing ───────────────────────────────────────────────────────
 * Kept outside React state: a typing frame arrives every couple of seconds
 * per person, and only the open thread cares. A context value would
 * re-render the sidebar badge and every chat screen on each one.
 */
const TYPING_EXPIRY_MS = 5000;
const NOBODY: readonly string[] = [];
const typingTimers = new Map<
  string,
  Map<string, ReturnType<typeof setTimeout>>
>();
const typingSnapshots = new Map<string, readonly string[]>();
const typingListeners = new Set<() => void>();

function publishTyping(conversationId: string) {
  const people = typingTimers.get(conversationId);
  if (people?.size) typingSnapshots.set(conversationId, [...people.keys()]);
  else typingSnapshots.delete(conversationId);
  for (const listener of typingListeners) listener();
}

export function setTyping(
  conversationId: string,
  staffId: string,
  isTyping: boolean,
) {
  let people = typingTimers.get(conversationId);
  const existing = people?.get(staffId);
  if (existing) clearTimeout(existing);
  if (!isTyping) {
    if (!people?.delete(staffId)) return;
    publishTyping(conversationId);
    return;
  }
  if (!people) {
    people = new Map();
    typingTimers.set(conversationId, people);
  }
  const wasTyping = !!existing;
  // Expires by itself: a tab that closes mid-sentence never says "stopped".
  people.set(
    staffId,
    setTimeout(
      () => setTyping(conversationId, staffId, false),
      TYPING_EXPIRY_MS,
    ),
  );
  if (!wasTyping) publishTyping(conversationId);
}

export function clearAllTyping() {
  for (const people of typingTimers.values()) {
    for (const timer of people.values()) clearTimeout(timer);
  }
  typingTimers.clear();
  typingSnapshots.clear();
  for (const listener of typingListeners) listener();
}

export const subscribeTyping = (onChange: () => void) => {
  typingListeners.add(onChange);
  return () => {
    typingListeners.delete(onChange);
  };
};

/** Stable between changes, as `useSyncExternalStore` requires. */
export const typingSnapshot = (conversationId: string | null) =>
  (conversationId && typingSnapshots.get(conversationId)) || NOBODY;

export const NOBODY_TYPING = NOBODY;
